import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { PopperProps, usePopper } from 'react-popper';
import { PopoverProps } from './types';

type PopperOptions = Pick<
  PopoverProps,
  | 'anchor'
  | 'arrow'
  | 'offset'
  | 'placement'
  | 'flipX'
  | 'flipY'
  | 'onFirstUpdate'
  | 'virtualAnchor'
  | 'positioningStrategy'
  | 'rootBoundary'
  | 'preventOverflow'
>;

const verticalFallbackPlacements = {
  top: ['bottom'],
  bottom: ['top'],
  left: [],
  right: [],
  'top-start': ['bottom-start'],
  'top-end': ['bottom-end'],
  'bottom-start': ['top-start'],
  'bottom-end': ['top-end'],
  'right-start': ['right-end'],
  'right-end': ['right-start'],
  'left-start': ['left-end'],
  'left-end': ['left-start'],
};

const horizontalFallbackPlacements = {
  top: [],
  bottom: [],
  left: ['right'],
  right: ['left'],
  'top-start': ['top-end'],
  'top-end': ['top-start'],
  'bottom-start': ['bottom-end'],
  'bottom-end': ['bottom-start'],
  'right-start': ['left-start'],
  'right-end': ['left-end'],
  'left-start': ['right-start'],
  'left-end': ['right-end'],
};

const getFlipModifier = (
  placement: Required<PopperOptions>['placement'],
  rootBoundary: Required<PopperOptions>['rootBoundary'],
  flipX: boolean,
  flipY: boolean,
) => {
  if (!flipX && !flipY) {
    return { name: 'flip', enabled: false };
  } else if (!flipX) {
    return {
      name: 'flip',
      enabled: true,
      options: {
        rootBoundary,
        fallbackPlacements: verticalFallbackPlacements[placement],
      },
    };
  } else if (!flipY) {
    return {
      name: 'flip',
      enabled: true,
      options: {
        rootBoundary,
        fallbackPlacements: horizontalFallbackPlacements[placement],
      },
    };
  }

  return {
    name: 'flip',
    enabled: true,
    options: {
      rootBoundary,
    },
  };
};

export const usePortalPopper = ({
  anchor,
  arrow,
  offset = 0,
  placement = 'bottom-start',
  flipX = true,
  flipY = true,
  onFirstUpdate,
  virtualAnchor,
  positioningStrategy = 'absolute',
  rootBoundary = 'viewport',
  preventOverflow,
}: PopperOptions) => {
  const [popperEl, setPopperEl] = useState<HTMLDivElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLDivElement | null>(null);
  const modifiers = useMemo(() => {
    const offsetY = Array.isArray(offset) ? offset[1] ?? 0 : offset;
    const offsetX = Array.isArray(offset) ? offset[0] ?? 0 : 0;
    const overflowOpts = [];
    if (preventOverflow) {
      if (Array.isArray(preventOverflow)) {
        overflowOpts.push(
          {
            name: 'preventOverflow',
            options: {
              mainAxis: true,
              padding: preventOverflow[0]!,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              altAxis: true,
              padding: preventOverflow[1]!,
            },
          },
        );
      } else {
        overflowOpts.push({
          name: 'preventOverflow',
          options: {
            mainAxis: true,
            altAxis: true,
            padding: preventOverflow,
          },
        });
      }
    }
    const mods: PopperProps<any>['modifiers'] = [
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [offsetX, !arrow ? offsetY : offsetY + 6],
        },
      },
      ...overflowOpts,
    ];

    const flipMod = getFlipModifier(placement, rootBoundary, flipX, flipY);
    const arrowMod = {
      name: 'arrow',
      enabled: true,
      options: {
        element: arrowEl,
        padding: 5, // 5px from the edges of the popper
      },
    };

    return mods.concat(arrow ? arrowMod : []).concat(flipMod ? flipMod : []);
  }, [arrow, offset, preventOverflow, placement, rootBoundary, flipX, flipY, arrowEl]);

  const { styles, attributes, forceUpdate } = usePopper(virtualAnchor ?? anchor, popperEl, {
    onFirstUpdate,
    placement,
    modifiers,
    strategy: positioningStrategy,
  });

  useEffect(() => {
    if (virtualAnchor) {
      forceUpdate?.();
    }
  }, [forceUpdate, virtualAnchor]);

  // Reposition the Popover if the anchor changes size
  useElementObserver(anchor, forceUpdate);

  // Reposition the Popover if the content changes
  useElementObserver(popperEl, forceUpdate);

  return {
    popperRef: setPopperEl,
    popperElement: popperEl,
    arrowRef: setArrowEl,
    styles,
    attributes,
  };
};

const useElementObserver = (
  element: HTMLElement | SVGGElement | null,
  update: (() => void) | null,
) => {
  const [anchorSize, setAnchorSize] = useState<{ width: number; height: number }>();

  useEffect(() => {
    // force popper update if the anchor size changes
    queueMicrotask(() => {
      update?.();
    });
  }, [anchorSize, update]);

  useEffect(() => {
    if (!element || !('ResizeObserver' in window)) return;
    let frameID: number;
    const elementObserver = new ResizeObserver(entries => {
      frameID = window.requestAnimationFrame(() => {
        for (const entry of entries) {
          // if the children of the anchor change, potentially update `anchorSize`
          setAnchorSize(anchorSize => {
            const { height, width } = entry.contentRect;
            const updatedAnchorSize = { width, height };
            // if sizes are equal, return current `anchorSize` object to prevent state update
            return isEqual(anchorSize, updatedAnchorSize) ? anchorSize : updatedAnchorSize;
          });
        }
      });
    });
    elementObserver.observe(element);
    return () => {
      elementObserver.disconnect();
      window.cancelAnimationFrame(frameID);
    };
  }, [element]);
};
